import type React from 'react';

import { ChevronSortIcon } from '@vlabs/icons';
import type { TOption } from '@vlabs/uikit/types';

import { useRoleListFilters } from '@vlabs/monokit/entities/roles/filters';
import { DropdownSelect } from '@vlabs/monokit/shared/ui/dropdown-select';

import { useSortRoleListTranslation } from './i18n';

type TRoleListSortValue =
  | 'name_asc'
  | 'name_desc'
  | 'create_time_desc'
  | 'create_time_asc'
  | 'update_time_desc'
  | 'update_time_asc';

function getDropdownValue(sort?: string, order?: string): TRoleListSortValue {
  if (sort === 'name' && order === 'desc') return 'name_desc';
  if (sort === 'create_time' && order === 'desc') return 'create_time_desc';
  if (sort === 'create_time' && order === 'asc') return 'create_time_asc';
  if (sort === 'update_time' && order === 'desc') return 'update_time_desc';
  if (sort === 'update_time' && order === 'asc') return 'update_time_asc';
  return 'name_asc';
}

function SortRoleListDropdown(): React.JSX.Element {
  const { t } = useSortRoleListTranslation();
  const { filters, updateFilters } = useRoleListFilters();

  const options: TOption<TRoleListSortValue>[] = [
    {
      label: t('name_asc'),
      value: 'name_asc',
    },
    {
      label: t('name_desc'),
      value: 'name_desc',
    },
    {
      label: t('create_time_desc'),
      value: 'create_time_desc',
    },
    {
      label: t('create_time_asc'),
      value: 'create_time_asc',
    },
    {
      label: t('update_time_desc'),
      value: 'update_time_desc',
    },
    {
      label: t('update_time_asc'),
      value: 'update_time_asc',
    },
  ];

  const value = getDropdownValue(filters.sort, filters.order);
  const selectedOption = options.find((option) => option.value === value);

  const onSelect = (nextValue: TRoleListSortValue): void => {
    switch (nextValue) {
      case 'name_desc':
        updateFilters({ order: 'desc', sort: 'name' });
        return;
      case 'create_time_desc':
        updateFilters({ order: 'desc', sort: 'create_time' });
        return;
      case 'create_time_asc':
        updateFilters({ order: 'asc', sort: 'create_time' });
        return;
      case 'update_time_desc':
        updateFilters({ order: 'desc', sort: 'update_time' });
        return;
      case 'update_time_asc':
        updateFilters({ order: 'asc', sort: 'update_time' });
        return;
      default:
        updateFilters({ order: 'asc', sort: 'name' });
    }
  };

  return (
    <DropdownSelect
      icon={<ChevronSortIcon />}
      label={selectedOption?.label ?? ''}
      onSelect={onSelect}
      options={options}
      value={value}
    />
  );
}

export { SortRoleListDropdown };
